import React, { useEffect, useState } from 'react';
import { WifiOff, Wifi } from 'lucide-react';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { getPendingExpenseCount } from '../../services/offlineDb';

/**
 * Floating indicator shown when the device is offline.
 * Also displays the count of queued (pending-sync) expenses.
 */
const OfflineIndicator: React.FC = () => {
  const isOnline = useOnlineStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [showReconnected, setShowReconnected] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
    }
    if (isOnline && wasOffline) {
      setShowReconnected(true);
      const t = setTimeout(() => setShowReconnected(false), 3000);
      return () => clearTimeout(t);
    }
  }, [isOnline, wasOffline]);

  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const count = await getPendingExpenseCount();
        if (active) setPendingCount(count);
      } catch {
        // IndexedDB unavailable
      }
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  if (isOnline && !showReconnected) return null;

  return (
    <div className="fixed top-2 left-1/2 -translate-x-1/2 z-[70] animate-slide-down">
      {!isOnline ? (
        <div className="flex items-center gap-2 rounded-full bg-amber-600 px-4 py-1.5 text-white text-xs font-medium shadow-md">
          <WifiOff className="h-3.5 w-3.5" />
          <span>
            You&apos;re offline
            {pendingCount > 0 && ` \u2022 ${pendingCount} expense${pendingCount > 1 ? 's' : ''} queued`}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded-full bg-green-600 px-4 py-1.5 text-white text-xs font-medium shadow-md">
          <Wifi className="h-3.5 w-3.5" />
          <span>Back online</span>
        </div>
      )}
    </div>
  );
};

export default OfflineIndicator;
